import { LoggerService } from 'app-core/src/services/logger.service';
import { inject, singleton } from 'tsyringe';
import {
  IAppEntryPointDelegate,
  AppEntryPointService,
} from './services/app-entry-point.service';
import { TesterWindow } from './windows/tester-window';
import {
  ContentRecommendationService,
  IContentRecommendationServiceDelegate,
} from 'app-core/src/services/content-recommendation-service/content-recommendation.service';
import { TimerService } from 'app-core/src/services/timer.service';
import { OWApplicationWrapper } from './ow-odk-wrappers/ow-application-wrapper';
import {
  INotificationWindowManagerService,
  kNameOfNotificationWindowManagerService,
} from 'app-core/src/interfaces/notification-window-manager-service.interface';
import { Application } from 'app-core/src/models/application.model';
import { Game } from 'app-core/src/models/game.model';

import { ContentRecommendationUserAction } from 'app-core/src/constants/content-recommendation-user-action.enum';
import {
  kDIRobloxListener,
  kDITOWExtensionWrapper,
  kDITOWGamesWrapper,
} from 'app-core/src/constants/injectionTokens';
import { IOwGamesWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-games-wrapper.interface';
import { IOWExtensionWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-extension-wrapper.interface';
import { IRobloxListener } from 'app-core/src/interfaces/ow-wrappers/roblox-listener.interface';
import {
  INotificationAnalyticsService,
  kNameOfNotificationAnalyticsService,
} from 'app-core/src/services/notification-analytics/notification-analytics.service';
import { NoRecommendationsReasons } from 'app-core/src/services/content-recommendation-service/no-recommendations-reasons.enums';
import { SubGameHandlerRegistry } from './sub-game-handlers/sub-game-handler-registry';

// Allow final logs/localStorage to be flushed
const kShutdownTimeoutForFlushingLocalStorageInMs = 100;

// Invokes the recommendation flow for `id`. When `id` is a sub-game candidate,
// `originalGameId` carries the parent gameId so it can be threaded onto the
// loaded Game.
type RecommendationInvoker = (
  id: number,
  originalGameId?: number,
) => Promise<void>;

// -----------------------------------------------------------------------------
@singleton()
export class BackgroundController
  implements IAppEntryPointDelegate, IContentRecommendationServiceDelegate
{
  private readonly logger = LoggerService.getCategory('BackgroundController');

  private notificationPresented = false;
  private isProbingSubGames = false;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly entryPointService: AppEntryPointService,
    @inject(kDITOWExtensionWrapper)
    private readonly owExtensionWrapper: IOWExtensionWrapper,
    private readonly contentRecommendationService: ContentRecommendationService,
    @inject(kNameOfNotificationWindowManagerService)
    private readonly notificationWinManager: INotificationWindowManagerService,
    private readonly testerWindow: TesterWindow,
    private readonly shutdownTimer: TimerService,
    private readonly owApplicationWrapper: OWApplicationWrapper,
    @inject(kNameOfNotificationAnalyticsService)
    private readonly analyticsService: INotificationAnalyticsService,
    @inject(kDITOWGamesWrapper)
    private readonly owGamesWrapper: IOwGamesWrapper,
    @inject(kDIRobloxListener)
    private readonly robloxListener: IRobloxListener,
    private readonly subGameHandlerRegistry: SubGameHandlerRegistry,
  ) {
    this.entryPointService.addListener(this);
    this.contentRecommendationService.addListener(this);
    this.notificationWinManager.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // NOTE: Because the BackgroundController implements the AppStateController
  // interface, we cannot instantiate any states (e.g. AppStateInit) from the
  // ctor of BackgroundController.
  public async run() {
    try {
      await this.entryPointService.initialize();
      await this.printStartupInformationToConsole();
    } catch (ex) {
      this.logger.error('Error:', ex);
    }
  }

  // ---------------------------------------------------------------------------
  private async printStartupInformationToConsole() {
    await this.owExtensionWrapper.initialize();
    const { manifest } = this.owExtensionWrapper;
    this.logger.info(`App version: ${manifest?.meta?.version}`);
  }

  // ---------------------------------------------------------------------------
  private async shutdown() {
    if (this.robloxListener.isActive()) {
      this.logger.info('Roblox listener is active - not closing app');
      return;
    }

    const hasOpenWindows = await this.hasOpenWindows();
    if (hasOpenWindows) {
      this.logger.info('We have open windows - not closing app');
      return;
    }

    this.logger.info('Shutting down ...');
    this.closeBackgroundWithDelay();
  }

  // ---------------------------------------------------------------------------
  private async hasOpenWindows(): Promise<boolean> {
    const isOpen = await this.notificationWinManager.isNotificationShown();
    if (isOpen) {
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  private async closeBackgroundWithDelay() {
    await this.shutdownTimer.sleep(kShutdownTimeoutForFlushingLocalStorageInMs);
    this.owApplicationWrapper.shutdown();
  }

  // ---------------------------------------------------------------------------
  private async shouldGameBeExcluded(gameId: number) {
    try {
      const game = await this.owGamesWrapper.getGameDBInfo(gameId);

      if (
        game?.gameInfo?.TypeString === 'Application' ||
        game?.installedGameInfo?.GameInfo?.TypeString === 'Application'
      ) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  private async handleGameStarted(gameId: number) {
    this.robloxListener.start(gameId);
    await this.runRecommendation(gameId, (id, originalGameId) =>
      this.contentRecommendationService.gameStarted(id, originalGameId),
    );
  }

  // ---------------------------------------------------------------------------
  // Tries the game's sub-game candidates before falling back to the original
  // gameId. The first candidate that produces a notification wins; if none do,
  // the original gameId is used. Per-game candidate selection lives in the
  // SubGameHandler registered for that gameId. When probing a sub-game, the
  // parent gameId is passed as `originalGameId` so it can be threaded into
  // the notification details.
  private async runRecommendation(
    gameId: number,
    invoke: RecommendationInvoker,
  ): Promise<void> {
    const handler = this.subGameHandlerRegistry.get(gameId);
    const candidates = (await handler?.getOrderedCandidates()) ?? [];

    if (candidates.length > 0) {
      this.logger.info(
        `Probing ${candidates.length} sub-game candidates for ${gameId}`,
      );

      const presented = await this.probeSubGameIds(gameId, candidates, invoke);
      if (presented) {
        return;
      }

      this.logger.info(
        `No sub-game candidate produced a recommendation, falling back to ${gameId}`,
      );
    }

    await invoke(gameId);
  }

  // ---------------------------------------------------------------------------
  private async probeSubGameIds(
    originalGameId: number,
    candidates: number[],
    invoke: RecommendationInvoker,
  ): Promise<boolean> {
    this.isProbingSubGames = true;
    try {
      for (const id of candidates) {
        this.notificationPresented = false;
        await invoke(id, originalGameId);
        if (this.notificationPresented) {
          this.logger.info(`Sub-game ${id} produced a recommendation`);
          return true;
        }
      }

      return false;
    } finally {
      this.isProbingSubGames = false;
    }
  }

  // ---------------------------------------------------------------------------
  // AppEntryPointDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onLaunchedWithDock() {
    await this.testerWindow.show();
  }

  // ---------------------------------------------------------------------------
  async onLaunchedWithGame(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game started ${gameId}, Ignoring`);
      this.shutdown();
      return;
    }

    await this.handleGameStarted(gameId);
  }

  // ---------------------------------------------------------------------------
  // OWGameListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onGameStarted(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game started ${gameId}, Ignoring`);
      return;
    }

    const hasOpenWindows = await this.hasOpenWindows();
    if (hasOpenWindows) {
      this.logger.info(
        'Game started while notification window is still open,' +
          ' closing notification window',
      );

      let applicationUid = '';
      let notificationGameId = gameId;
      let originalGameId: number | undefined;

      const activeNotificationInfo =
        this.notificationWinManager.getNotificationInfo();

      if (activeNotificationInfo) {
        applicationUid = activeNotificationInfo.applicationUid;
        notificationGameId =
          activeNotificationInfo.targetGameId || activeNotificationInfo.gameId;
        originalGameId = activeNotificationInfo.originalGameId;
      }

      this.analyticsService.reportContentRecommend(
        notificationGameId,
        applicationUid,
        ContentRecommendationUserAction.IgnoredByLaunchingGame,
        undefined,
        originalGameId,
      );

      this.notificationWinManager.close();
    }

    await this.handleGameStarted(gameId);
  }

  // ---------------------------------------------------------------------------
  async onGameEnded(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game Ended ${gameId}, Ignoring`);
      return;
    }

    const wasRobloxActive = this.robloxListener.isHandlingGame(gameId);
    if (wasRobloxActive) {
      this.robloxListener.stop();
    }

    await this.runRecommendation(gameId, (id, originalGameId) =>
      this.contentRecommendationService.gameEnded(id, originalGameId),
    );
  }

  // ---------------------------------------------------------------------------
  // IContentRecommendationServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onNoRecommendations(reason: NoRecommendationsReasons) {
    // While probing sub-game candidates, suppress shutdown — the probe loop
    // drives the outcome and may still fall back to the original gameId.
    if (this.isProbingSubGames) {
      return;
    }

    // For ow we do not want to shutdown in case we are configured
    // to wait until the end
    if (reason === NoRecommendationsReasons.ConfiguredForEnd) {
      return;
    }

    this.shutdown();
  }

  // ---------------------------------------------------------------------------
  async onShowNotification(game: Game, application: Application) {
    this.notificationPresented = true;
    this.notificationWinManager.show(game, application);
  }

  // ---------------------------------------------------------------------------
  // INotificationWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onNotificationWindowClosed() {
    this.logger.info('Notification Window closed, Shutting down in 3 seconds');
    setTimeout(() => {
      this.shutdown();
    }, 3000);
  }
}
